<template>
  <view-container>
    <browse-tabs :model-value="BROWSE_TABS.MODS" view />

    <base-three-quarters-column col-spacing-y="0">
      <template #first>
        <view-header-breadcrumbs
          :library-breadcrumb="libraryBreadcrumb"
          :item-type="ITEM_TYPES.MOD"
          :mod="mod"
        />
        <div
          class="tw-mb-5"
          :class="{ 'lg:tw-hidden': Settings.nameRightSide }"
        >
          <h3 class="tw-leading-tight tw-text-h2 tw-font-medium">
            {{ mod?.name || '&nbsp;' }}
          </h3>
          <p v-if="mod" class="tw-opacity-70">
            {{ $t('published') }}
            <base-time-tooltip :timestamp="mod.date_live">{{
              humanDateFormat(mod.date_live)
            }}</base-time-tooltip>
            &bull;
            {{ $t('updated') }}
            <base-time-tooltip :timestamp="mod.date_updated || mod.date_live">
              {{ timeSince(mod.date_updated || mod.date_live) }}
              {{ $t('ago') }}
            </base-time-tooltip>
          </p>
        </div>

        <view-images
          :mod-id="mod?.id"
          :loading="loadingMod"
          :logo-image="mod?.logo"
          :images="modImages"
          :youtube="modYoutube"
          :sketchfab="modSketchfab"
        />
      </template>

      <template #second>
        <view-overview
          :panel-loading="loadingMod"
          :game="game"
          :mod="mod"
          :dependencies="deps"
        />

        <div class="tw-flex tw-flex-col tw-space-y-8 tw-mt-8">
          <!-- show for lg screen down -->
          <view-mod-stats
            :panel-loading="loadingMod"
            :mod="mod"
            :game="game"
            :class="Classes.SUMMARY"
          />

          <view-tags
            v-if="tags.length && !loadingMod"
            :class="Classes.TAGS"
            no-margin
            :tags="tags"
            @tag:filter="(input) => tagFilter(input)"
          />

          <!-- show for lg screen down -->
          <view-contributors :mod="mod" :panel-loading="loadingMod || !mod" />

          <view-files
            :panel-loading="loadingMod"
            :game="game"
            :mod="mod"
            :class="Classes.FILE"
            class="tw-hidden lg:tw-block"
          />

          <view-collections-with-mod
            v-if="Settings.showCollections"
            :panel-loading="loadingMod || !mod"
            :game="game"
          />

          <view-reuse-permissions
            v-if="allowUgcPermissions(game.community_options)"
            :game="game"
            :mod="mod"
          />

          <view-flag-button
            :resource="RESOURCE_MODS"
            :resource-id="mod?.id"
            :name="mod?.name"
            :class="Classes.DETAIL_REPORT_BUTTON"
            class="tw-hidden lg:tw-block"
          />

          <view-files
            :panel-loading="loadingMod"
            :game="game"
            :mod="mod"
            :class="Classes.FILE"
            class="lg:tw-hidden"
            expandable
          />

          <view-flag-button
            :resource="RESOURCE_MODS"
            :resource-id="mod?.id"
            :name="mod?.name"
            :class="Classes.DETAIL_REPORT_BUTTON"
            class="lg:tw-hidden"
          />
        </div>
      </template>

      <template v-if="mod" #[discussionSlotName]>
        <base-switcher
          v-if="switcherTabs.length > 1"
          class="tw-mb-4 lg:tw-mb-8 tw-text-h6"
          :class="Classes.VIEW_NAVBAR"
          :options="switcherTabs"
          :model-value="tab"
          @input="(_, val) => updateTab(val)"
        />
        <div
          v-show="tab === TAB_TYPES.DESCRIPTION.value"
          :class="[Classes.DESCRIPTION, sectionClasses]"
        >
          <view-description
            :mod="mod"
            :panel-loading="loadingMod"
            :show-header="switcherTabs.length === 1"
          />
        </div>
        <div
          v-show="tab === TAB_TYPES.DISCUSSION.value"
          :class="[Classes.COMMENTS, sectionClasses]"
        >
          <discussion-panel
            :type="DISCUSSION_TYPE_MODS"
            :resource="mod"
            :panel-loading="loadingMod"
            :game-name-id="game?.name_id"
            @update:count="(val) => (discussionCount = val)"
          />
        </div>
        <div
          v-if="deps.length"
          v-show="tab === TAB_TYPES.DEPENDENCIES.value"
          :class="[Classes.DEPENDENCIES, sectionClasses]"
        >
          <view-mod-list
            :game="game"
            :mod="mod"
            :loading="loadingMod"
            :mods-total="deps.length"
            :mod-list="deps"
          />
        </div>
      </template>
    </base-three-quarters-column>
  </view-container>
</template>

<script>
import ViewCollectionsWithMod from '@components/Mod/ViewCollectionsWithMod.vue'
import ViewHeaderBreadcrumbs from '@components/View/ViewHeaderBreadcrumbs.vue'
import ViewReusePermissions from '@components/Mod/ViewReusePermissions.vue'
import DiscussionPanel from '@components/Discussion/DiscussionPanel.vue'
import { MOD_BROWSE_ROUTE, LIBRARY_ROUTE } from '@config/routeNames.js'
import ViewModList from '@shared/components/Collection/ViewModList.vue'
import ViewContributors from '@components/Mod/ViewContributors.vue'
import { getDependenciesRequest, getModRequest } from '@services'
import ViewDescription from '@components/Mod/ViewDescription.vue'
import ViewFlagButton from '@components/View/ViewFlagButton.vue'
import BrowseTabs from '@components/Navigation/BrowseTabs.vue'
import ViewContainer from '@components/View/ViewContainer.vue'
import { humanDateFormat, timeSince } from '@helpers/utils.js'
import ViewModStats from '@components/Mod/ViewModStats.vue'
import { onBeforeMount, computed, provide, ref } from 'vue'
import ViewOverview from '@components/Mod/ViewOverview.vue'
import ViewImages from '@components/View/ViewImages.vue'
import ViewFiles from '@components/Mod/ViewFiles.vue'
import ViewTags from '@components/View/ViewTags.vue'
import { getTags } from '@helpers/localizedTags.js'
import Classes from '@shared/js/config/css.js'
import { gameStore, modStore } from '@stores'
import { useBreakpoints } from '@vueuse/core'
import { Settings } from '@config/game.js'
import { BREAKPOINTS } from '@config'
import { useI18n } from 'vue-i18n'
import {
  GAME_DISALLOW_DEPENDENCIES,
  DISCUSSION_TYPE_MODS,
  RESOURCE_MODS,
  BROWSE_TABS,
  ITEM_TYPES,
} from '@config/options.js'
import {
  allowDownvoteModComments,
  allowGameModComments,
  allowUgcPermissions,
  allowModComments,
} from '@helpers/bitHelper.js'
import {
  useResourceValue,
  usePermissions,
  useRoute,
  useAsync,
} from '@composables'

const sectionClasses =
  'game-tile-background-color game-tile-radius game-tile-border tw-p-4'

export default {
  components: {
    ViewCollectionsWithMod,
    ViewHeaderBreadcrumbs,
    ViewReusePermissions,
    ViewContributors,
    DiscussionPanel,
    ViewDescription,
    ViewFlagButton,
    ViewContainer,
    ViewModStats,
    ViewOverview,
    ViewModList,
    BrowseTabs,
    ViewImages,
    ViewFiles,
    ViewTags,
  },
  emits: ['tag:filter'],
  setup() {
    const { getModId, push, replace, routerHistory, routeQuery } = useRoute()

    const modNameId = getModId().value
    const { greaterOrEqual } = useBreakpoints(BREAKPOINTS)
    const lgAndUp = greaterOrEqual('lg')
    const discussionCount = ref(0)

    const { isGameModerator } = usePermissions()
    const { ugcName } = useResourceValue()
    const { t } = useI18n()

    let ugcNameText = ugcName(null, false, false).value

    if (ugcNameText === 'mods') ugcNameText = t('mod', 2)
    else if (ugcNameText === 'levels') ugcNameText = t('level', 2)

    // Stores
    const { addMod, getModByNameId } = modStore()
    const { getPublicGame } = gameStore()

    const game = getPublicGame()
    const mod = getModByNameId(game.value.name_id, modNameId)
    const deps = ref([])

    const libraryBreadcrumb = computed(() =>
      routerHistory.value.back?.includes('/library')
    )

    const gameAllowDownvote = computed(() =>
      allowDownvoteModComments(game.value.community_options)
    )

    provide('gameAllowDownvote', gameAllowDownvote)

    const {
      loading: loadingMod,
      run: runfetchMod,
      data: modData,
      error: modsErrors,
    } = useAsync(() => getModRequest(modNameId))

    const {
      data: depsData,
      run: runFetchDeps,
      error: depsErrors,
    } = useAsync(() => getDependenciesRequest(game.value.name_id, modNameId))

    const TAB_TYPES = computed(() => ({
      DESCRIPTION: { text: t('description'), value: 'description' },
      DISCUSSION: { text: t('discussion'), value: 'discussion' },
      DEPENDENCIES: { text: t('dependencies'), value: 'dependencies' },
    }))

    const tab = ref(routeQuery.value.tab ?? TAB_TYPES.value.DESCRIPTION.value)

    const modImages = computed(() =>
      mod.value?.media?.images ? mod.value.media.images : []
    )

    const modYoutube = computed(() =>
      mod.value?.media?.youtube ? mod.value.media.youtube : []
    )

    const modSketchfab = computed(() =>
      mod.value?.media?.sketchfab ? mod.value.media.sketchfab : []
    )

    const discussionSlotName = computed(() =>
      lgAndUp.value ? 'first-bottom' : 'second-bottom'
    )

    const tags = computed(() => {
      if (!mod.value || !game.value) return []

      return getTags(game.value.tag_options, isGameModerator.value, mod)
    })

    const showComments = computed(
      () =>
        Settings.value.showComments &&
        allowGameModComments(game.value.community_options) &&
        allowModComments(mod.value?.community_options)
    )

    const switcherTabs = computed(() => {
      const tabs = [
        TAB_TYPES.value.DESCRIPTION,
        {
          ...TAB_TYPES.value.DISCUSSION,
          count: discussionCount.value,
        },
        {
          ...TAB_TYPES.value.DEPENDENCIES,
          count: deps.value.length || 0,
          disabled: !deps.value.length,
          tooltip: deps.value.length
            ? ''
            : t('no_dependencies', { ugc: ugcName(game, true, true).value }),
        },
      ]

      if (!showComments.value) tabs.splice(1, 1)

      if (game.value.dependency_option === GAME_DISALLOW_DEPENDENCIES) {
        tabs.pop()
      }

      return tabs
    })

    function tagFilter(name) {
      push({
        name: MOD_BROWSE_ROUTE,
        query: { tags: name },
      })
    }

    onBeforeMount(async () => {
      if (!mod.value) {
        await runfetchMod()

        if (!modsErrors.value) {
          addMod(game.value.name_id, modData.value)
        }
      }

      fetchDeps()
    })

    async function fetchDeps() {
      if (!mod.value.dependencies) return

      await runFetchDeps()

      if (!depsErrors.value) {
        deps.value = depsData.value.data
      }
    }

    function updateTab(val) {
      tab.value = val

      replace({
        query: { tab: val },
      })
    }

    return {
      DISCUSSION_TYPE_MODS,
      allowUgcPermissions,
      discussionSlotName,
      libraryBreadcrumb,
      humanDateFormat,
      discussionCount,
      sectionClasses,
      LIBRARY_ROUTE,
      RESOURCE_MODS,
      modSketchfab,
      showComments,
      switcherTabs,
      BROWSE_TABS,
      modYoutube,
      loadingMod,
      ITEM_TYPES,
      updateTab,
      timeSince,
      modImages,
      tagFilter,
      TAB_TYPES,
      Settings,
      Classes,
      game,
      tags,
      deps,
      mod,
      tab,
    }
  },
}
</script>
